// src/lib/subscriptionGating.js

/**
 * Tier-based league and insight access rules
 * Based on ANALYTICS_SUBSCRIPTION_PLAN_V2.md
 */

// League access by tier
export const TIER_LEAGUES = {
  starter: ['Premier League'],
  observer: ['Premier League', 'Championship', 'League One', 'League Two'],
  coach: ['Premier League', 'Championship', 'League One', 'League Two'], // Observer Plus (future)
  strategist: [
    'Premier League',
    'Championship',
    'League One',
    'League Two',
    'La Liga',
    'Bundesliga',
    'Serie A',
    'Ligue 1',
  ],
  tactician: [
    'Premier League',
    'Championship',
    'League One',
    'League Two',
    'La Liga',
    'Bundesliga',
    'Serie A',
    'Ligue 1',
  ], // Strategist Plus (future)
  analyst: ['*'], // All football
  architect: ['*'],
  master: ['*'], // + NFL
  grandmaster: ['*'], // Everything
};

// Insight category access by tier
export const TIER_INSIGHTS = {
  match_context: ['starter', 'observer', 'coach', 'strategist', 'tactician', 'analyst', 'architect', 'master', 'grandmaster'],
  form_momentum: ['observer', 'coach', 'strategist', 'tactician', 'analyst', 'architect', 'master', 'grandmaster'],
  personal_retrospectives: ['observer', 'coach', 'strategist', 'tactician', 'analyst', 'architect', 'master', 'grandmaster'],
  head_to_head: ['strategist', 'tactician', 'analyst', 'architect', 'master', 'grandmaster'],
  fixture_difficulty: ['strategist', 'tactician', 'analyst', 'architect', 'master', 'grandmaster'],
  volatility: ['strategist', 'tactician', 'analyst', 'architect', 'master', 'grandmaster'],
  scenario_context: ['tactician', 'grandmaster'],
  league_meta: ['analyst', 'architect', 'master', 'grandmaster'],
  weather_context: ['analyst', 'architect', 'master', 'grandmaster'],
  cross_sport: ['master', 'grandmaster'],
  behavioural_evolution: ['grandmaster'],
};

// Tier display names
export const TIER_NAMES = {
  starter: 'Starter',
  observer: 'Observer',
  coach: 'Coach',
  strategist: 'Strategist',
  tactician: 'Tactician',
  analyst: 'Analyst',
  architect: 'Architect',
  master: 'Master',
  grandmaster: 'Grandmaster',
};

// Tier hierarchy for comparison (lower index = lower tier)
const TIER_HIERARCHY = [
  'starter',
  'observer',
  'coach',
  'strategist',
  'tactician',
  'analyst',
  'architect',
  'master',
  'grandmaster',
];

/**
 * Check if user has access to a specific league's analytics
 * NOTE: All users can VIEW fixtures, but analytics depth varies by tier
 * @param {string} userTier - User's subscription tier
 * @param {string} league - League name (e.g., 'Premier League', 'La Liga')
 * @returns {boolean}
 */
export function hasLeagueAccess(userTier, league) {
  const allowedLeagues = TIER_LEAGUES[userTier] || [];
  
  // Wildcard access for top tiers
  if (allowedLeagues.includes('*')) {
    return true;
  }

  return allowedLeagues.includes(league);
}

/**
 * Get analytics depth level for a user's tier
 * Starter: basic (simplified analytics)
 * Observer/Strategist: standard (detailed analytics for their leagues)
 * Plus modes: advanced (future - deeper insights)
 * @param {string} userTier - User's subscription tier
 * @returns {string} - 'basic', 'standard', 'advanced', 'premium'
 */
export function getAnalyticsDepth(userTier) {
  const depthMap = {
    starter: 'basic', // Simplified analytics, Premier League only
    observer: 'standard', // Detailed analytics, English 4 leagues
    coach: 'advanced', // Observer Plus - Advanced analytics, English 4 leagues (future)
    strategist: 'standard', // Same detailed analytics as Observer, but 8 leagues
    tactician: 'advanced', // Strategist Plus - Advanced analytics, 8 leagues (future)
    analyst: 'premium', // System-level insights, all football
    architect: 'premium',
    master: 'premium', // + NFL
    grandmaster: 'premium', // Everything
  };
  return depthMap[userTier] || 'basic';
}

/**
 * Check if user has access to a specific insight category
 * @param {string} userTier - User's subscription tier
 * @param {string} insightType - Insight category (e.g., 'form_momentum', 'head_to_head')
 * @returns {boolean}
 */
export function hasInsightAccess(userTier, insightType) {
  const allowedTiers = TIER_INSIGHTS[insightType] || [];
  return allowedTiers.includes(userTier);
}

/**
 * Get the minimum tier required for a league
 * @param {string} league - League name
 * @returns {string} - Minimum tier name
 */
export function getMinimumTierForLeague(league) {
  // Check each tier from lowest to highest
  for (const tier of TIER_HIERARCHY) {
    if (hasLeagueAccess(tier, league)) {
      return tier;
    }
  }
  return 'grandmaster'; // Fallback
}

/**
 * Get the minimum tier required for an insight
 * @param {string} insightType - Insight category
 * @returns {string} - Minimum tier name
 */
export function getMinimumTierForInsight(insightType) {
  const allowedTiers = TIER_INSIGHTS[insightType] || [];
  
  if (allowedTiers.length === 0) {
    return 'grandmaster'; // Fallback
  }

  // Find lowest tier in hierarchy that has access
  for (const tier of TIER_HIERARCHY) {
    if (allowedTiers.includes(tier)) {
      return tier;
    }
  }

  return allowedTiers[0]; // Fallback to first allowed tier
}

/**
 * Get user-friendly upgrade message for locked content
 * @param {string} requiredTier - Tier needed for access
 * @param {string} contentType - 'league' or 'insight'
 * @param {string} contentName - Name of locked content
 * @returns {object} - { title, message, ctaText }
 */
export function getUpgradeMessage(requiredTier, contentType, contentName) {
  const tierName = TIER_NAMES[requiredTier] || requiredTier;

  if (contentType === 'league') {
    return {
      title: `${contentName} Analytics`,
      message: `Unlock ${contentName} insights with ${tierName} tier`,
      ctaText: `Upgrade to ${tierName}`,
    };
  }

  if (contentType === 'insight') {
    return {
      title: 'Premium Analytics',
      message: `${contentName} available in ${tierName} tier and above`,
      ctaText: `Upgrade to ${tierName}`,
    };
  }

  return {
    title: 'Upgrade Required',
    message: `This feature requires ${tierName} tier`,
    ctaText: `Upgrade to ${tierName}`,
  };
}

/**
 * Compare two tiers
 * @param {string} tier1 - First tier
 * @param {string} tier2 - Second tier
 * @returns {number} - Negative if tier1 < tier2, 0 if equal, positive if tier1 > tier2
 */
export function compareTiers(tier1, tier2) {
  const index1 = TIER_HIERARCHY.indexOf(tier1);
  const index2 = TIER_HIERARCHY.indexOf(tier2);
  return index1 - index2;
}

/**
 * Check if user can upgrade to a specific tier
 * @param {string} currentTier - User's current tier
 * @param {string} targetTier - Tier to upgrade to
 * @returns {boolean}
 */
export function canUpgradeTo(currentTier, targetTier) {
  return compareTiers(currentTier, targetTier) < 0;
}

/**
 * Get all leagues accessible by a tier
 * @param {string} tier - User's tier
 * @returns {string[]} - Array of league names
 */
export function getAccessibleLeagues(tier) {
  const leagues = TIER_LEAGUES[tier] || [];
  
  if (leagues.includes('*')) {
    // Return all defined leagues
    return Object.values(TIER_LEAGUES)
      .flat()
      .filter((league) => league !== '*');
  }

  return leagues;
}

/**
 * Get all insights accessible by a tier
 * @param {string} tier - User's tier
 * @returns {string[]} - Array of insight types
 */
export function getAccessibleInsights(tier) {
  return Object.keys(TIER_INSIGHTS).filter((insightType) =>
    hasInsightAccess(tier, insightType)
  );
}
